import styled from "styled-components";
import { Card } from "@mui/material";
import {
  QUERIES,
  SPACING,
  SPACING_RESPONSIVE,
  TOUCH_TARGET,
} from "../../../../../utils/constants";
import { BaseButton } from "../../../../../components/Button";

export const HeroCard = styled(Card)<{ $isDeactivated: boolean }>`
  && {
    position: relative;
    display: flex;
    flex-direction: column;
    border-radius: 12px;
    box-shadow: var(--shadow-small);
    border: 1px solid var(--color-card-border);
    background: ${({ $isDeactivated }) =>
      $isDeactivated
        ? "var(--color-card-background-deactivated)"
        : "var(--hero-background)"};

    font-family: "Crimson Pro", sans-serif;
    padding: ${SPACING_RESPONSIVE.mobile.cardPadding}px;
    padding-bottom: 0;

    @media ${QUERIES.tabletLargeAndUp} {
      padding: ${SPACING_RESPONSIVE.tablet.cardPadding}px;
      padding-bottom: ${SPACING.xs}px;
    }

    @media ${QUERIES.laptopAndUp} {
      padding: ${SPACING_RESPONSIVE.desktop.cardPadding}px;
      padding-bottom: ${SPACING.xs}px;
    }
  }
`;

export const HeroHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;

  @media ${QUERIES.tabletLargeAndUp} {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    gap: ${SPACING.lg}px;
  }
`;

export const HeroIdentity = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
  min-width: 0;
`;

export const IdentityRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm + SPACING.xs}px;
  flex-wrap: wrap;
`;

export const HeroName = styled.h1`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-display);
  font-weight: 700;
  letter-spacing: -0.015em;
  color: var(--color-text-default);
  margin: 0;
`;

export const HeroStatusBadge = styled.span<{ $isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.xs}px;
  padding: ${SPACING.xs}px ${SPACING.sm}px;
  border-radius: 999px;
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background-color: ${({ $isActive }) =>
    $isActive
      ? "color-mix(in srgb, var(--color-success) 14%, transparent)"
      : "color-mix(in srgb, var(--color-text-muted) 14%, transparent)"};
  color: ${({ $isActive }) =>
    $isActive ? "var(--color-success)" : "var(--color-text-muted)"};

  &::before {
    content: "";
    width: 0.4em;
    height: 0.4em;
    border-radius: 50%;
    background-color: currentColor;
  }
`;

export const HeroSubtitle = styled.p`
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
  margin: 0;
`;

export const HeaderActions = styled.div`
  display: flex;
  gap: ${SPACING.sm}px;

  & > * {
    flex: 1;
  }

  & button {
    width: 100%;
    justify-content: center;
  }

  @media ${QUERIES.tabletLargeAndUp} {
    flex-shrink: 0;

    & > * {
      flex: none;
    }

    & button {
      width: auto;
    }
  }
`;

export const HeroMain = styled.div<{ $isSingle: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${SPACING.lg}px;
  margin: ${SPACING.lg}px 0 ${SPACING.md}px;

  @media ${QUERIES.laptopAndUp} {
    grid-template-columns: ${({ $isSingle }) =>
      $isSingle ? "1fr" : "1.15fr 1fr"};
    gap: ${SPACING.xxl}px;
    margin-bottom: ${SPACING.lg}px;
  }
`;

export const ValuePair = styled.div<{ $isSingle: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${SPACING.md}px;
  align-items: start;
  min-width: 0;

  @media ${QUERIES.tabletLargeAndUp} {
    grid-template-columns: ${({ $isSingle }) =>
      $isSingle ? "1fr" : "1.2fr 1fr"};
    gap: ${SPACING.lg}px;
  }
`;

export const ValueBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
  min-width: 0;
`;

export const ValueLabel = styled.span`
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

export const ValueAmount = styled.span<{ $negative: boolean }>`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-display-xl);
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.08;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  color: ${({ $negative }) =>
    $negative ? "var(--color-error)" : "var(--color-text-default)"};
`;

export const GainChip = styled.span<{ $negative: boolean }>`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: ${SPACING.sm}px;
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-base);
  font-weight: 600;
  color: ${({ $negative }) =>
    $negative ? "var(--color-error)" : "var(--color-success)"};

  svg {
    font-size: var(--text-lg);
  }
`;

export const SideFigure = styled.div<{ $isMuted?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
  min-width: 0;
  padding-top: ${SPACING.md}px;
  border-top: 1px solid var(--color-border);
  color: ${({ $isMuted }) =>
    $isMuted ? "var(--color-text-secondary)" : "var(--color-text-default)"};

  @media ${QUERIES.tabletLargeAndUp} {
    padding-top: 0;
    padding-left: ${SPACING.lg}px;
    border-top: 0;
    border-left: 1px solid var(--color-border);
  }
`;

export const SideAmount = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-xxl);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.1;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;

  @media ${QUERIES.tabletLargeAndUp} {
    margin-top: ${SPACING.sm}px;
  }
`;

export const SideLabelRow = styled.span`
  display: flex;
  align-items: center;
  gap: ${SPACING.xxs}px;
`;

export const InfoButton = styled(BaseButton)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${TOUCH_TARGET}px;
  height: ${TOUCH_TARGET}px;
  margin-block: -${SPACING.md}px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: none;
  color: var(--color-text-secondary);
  font-size: var(--text-md);
  cursor: help;

  &:hover {
    color: var(--color-primary);
  }
`;

export const SideError = styled.p`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SPACING.sm}px;
  margin: 0;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-error);
`;

export const InfoContent = styled.span`
  display: grid;
  gap: ${SPACING.sm}px;
  font-size: var(--text-sm);
`;

export const InfoSplit = styled.span`
  display: grid;
  gap: ${SPACING.xs}px;
  padding-block: ${SPACING.sm}px;
  border-block: 1px solid color-mix(in srgb, currentColor 25%, transparent);
`;

export const InfoLine = styled.span`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  column-gap: ${SPACING.md}px;

  strong {
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
`;

export const InfoLineNote = styled.span`
  flex-basis: 100%;
  opacity: 0.8;
`;

export const InfoText = styled.span`
  display: block;
`;

export const Disclosure = styled.details`
  @media ${QUERIES.tabletLargeAndUp} {
    & > summary {
      display: none;
    }
  }
`;

export const DisclosureSummary = styled.summary`
  display: flex;
  align-items: center;
  min-height: ${TOUCH_TARGET}px;
  border-top: 1px solid var(--color-border);
  color: var(--color-primary);
  font-size: var(--text-base);
  font-weight: 600;
  cursor: pointer;
  list-style: none;

  &::-webkit-details-marker {
    display: none;
  }
`;
